import { effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LeafletControlLayersConfig } from '@bluehalo/ngx-leaflet';
import { dsDtos, userDtos, webDtos } from '@vectronic/openApi-customer';
import { LayoutResizeEventService } from '@vectronic/ui';
import { MobileService, Translator } from '@vectronic/util';
import { MarkerClusterGroup } from 'leaflet';
import { L } from '../leaflet-setup/leaflet-setup';
import {
  CanvasMarkerData,
  TrackPolyline,
  UnifiedCanvasClickEvent,
  UnifiedCanvasTrackClickEvent,
  UnifiedData,
  UnifiedDrawToggles,
  UnifiedMarkerCanvasLayer,
} from '../unifiedCanvasLayer/unified-marker-canvas-layer.service';
import { DrawingManagerService } from '../virtualFence/drawing-manager.service';
import { MapBaseLayersService } from './map-base-layers.service';
import { MapDistanceMeasurementService } from './map-distance-measurement.service';
import { MapFilterService } from './map-filter.service';
import { MapMarkerService } from './map-marker.service';

interface RenderedMapItem {
  source: webDtos.MapPositionsList;
  markerClusterLayer: MarkerClusterGroup;
  lightConditionClusterLayer: MarkerClusterGroup;
  mortalityData: CanvasMarkerData[];
  speciesIconData: CanvasMarkerData[];
  standardMarkersData: CanvasMarkerData[];
  endpointMarkersData: CanvasMarkerData[];
  allLightConditionsData: CanvasMarkerData[];
  trackPolylines: TrackPolyline[];
}

@Injectable({ providedIn: 'root' })
export class MapService {
  private mapDistanceMeasurementService = inject(MapDistanceMeasurementService);
  private mapBaseLayersService = inject(MapBaseLayersService);
  mapMarkerService = inject(MapMarkerService);
  private mobileService = inject(MobileService);
  private mapFilterService = inject(MapFilterService);
  layoutResizeEventService = inject(LayoutResizeEventService);
  private drawingManagerService = inject(DrawingManagerService);
  map: L.Map;
  /** Base-map instances currently alive; the last one attached owns the state. */
  private attachedMaps: L.Map[] = [];
  /** Preferences snapshot per attached map, restored when its map survives a teardown. */
  private attachedPreferences = new Map<L.Map, userDtos.UserPreferences>();
  /** Maps that already got a scale bar, so re-mounting layers cannot stack a second one. */
  private scaleBarMaps = new WeakSet<L.Map>();
  private mapPositions: webDtos.MapPositionsList[] = [];
  userPreferences: userDtos.UserPreferences;
  private zoomElement: Element;
  private unifiedCanvasLayer?: UnifiedMarkerCanvasLayer | null; // Now manages all canvas drawing
  // Whether the unified canvas layer resolves map clicks as collar/track hits.
  // Suspended while a drawing tool (distance measurement, virtual-fence
  // editing) needs the raw map clicks for itself.
  private unifiedCanvasInterceptClicks = true;
  selectedMapItemId = signal<number | undefined>(undefined);
  private requestedFocusItemId = signal<number | undefined>(undefined);
  measurementCompleted = signal<boolean>(false);
  private zoomDebounceTimer?: ReturnType<typeof setTimeout>;
  private previousZoom: number;
  isLoading = signal(false);
  mapInstance = signal<L.Map | undefined>(undefined);
  layerChanges = signal<number>(0);
  // Use a separate cluster group for each track

  // Use a separate cluster group for each track
  private markerClusterLayer: MarkerClusterGroup[] = [];
  private lightConditionClusterLayers: MarkerClusterGroup[] = [];
  private renderedMapItems = new Map<number, RenderedMapItem>();
  private lastSpeciesMap?: Map<number, dsDtos.AnimalSpecies>;

  // Layer definitions simplified for the unified canvas approach
  layers = {
    polygons: new L.FeatureGroup(),
    markers: new L.LayerGroup(), // Holds standard marker clusters (DOM)
    lightCondition: new L.LayerGroup(), // Holds light condition clusters (DOM)
    unifiedCanvas: new L.FeatureGroup(), // Holds the single canvas element for non-clustered drawing
  };

  options: L.MapOptions = {
    zoom: 3,
    minZoom: 3,
    maxZoom: 20,
    center: L.latLng(30, 0),
    layers: [],
    renderer: L.canvas?.(),
    preferCanvas: true,
    zoomAnimation: true,
    markerZoomAnimation: false,
    fadeAnimation: true,
    maxBounds: L.latLngBounds(L.latLng(-85.0511, -220), L.latLng(85.0511, 220)),
    maxBoundsViscosity: 0.5,
    zoomControl: false,
  };

  layersControl: LeafletControlLayersConfig = {
    baseLayers: {},
    overlays: {},
  };

  constructor() {
    this.layersControl.baseLayers = {};

    //TOBE TESTED
    this.layoutResizeEventService.resizeEndedEvent$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.refreshMap());

    // Distance measurement and virtual-fence editing toggle the drawing mode
    // exactly while they must consume the raw map clicks (vertex placement,
    // handle dragging), so the mode signal drives the canvas layer's click
    // interception instead of the old pane pointer-events toggling.
    effect(() => {
      const mode = this.drawingManagerService.activeDrawingMode();
      this.setUnifiedCanvasClickInterception(!mode);
    });
  }

  private setUnifiedCanvasClickInterception(enabled: boolean) {
    this.unifiedCanvasInterceptClicks = enabled;
    this.unifiedCanvasLayer?.setInterceptClicks(enabled);
  }

  private handleMapPreclick = (e?: L.LeafletMouseEvent) => {
    if (this.drawingManagerService.activeDrawingMode()) return;
    // Clicks on a collar marker/track manage their own selection (select or
    // toggle off), so the selection must survive this preclick for the
    // layer's click handling to see it. Anything else on the map closes the
    // details panel.
    if (this.unifiedCanvasLayer?.hasHitAt(e?.latlng)) return;
    this.setSelectedMapItem(undefined);
  };

  /**
   * Called by ESRI feature click handlers: the collar canvas paints above the
   * feature paths but is DOM-transparent, so a click on a collar marker/track
   * that overlaps a feature lands on the feature path — which opens its own
   * popup and stops the map-level click, leaving the canvas layer unaware.
   * When the canvas has a hit at the click position the canvas wins: the
   * caller closes its just-opened popup and the canvas layer fires the
   * select/toggle events.
   */
  handleCanvasHitUnderFeatureClick(latlng: L.LatLng, sourceEvent?: L.LeafletMouseEvent) {
    if (!this.unifiedCanvasInterceptClicks || !this.unifiedCanvasLayer) return false;
    if (!this.unifiedCanvasLayer.hasHitAt(latlng)) return false;
    this.unifiedCanvasLayer.resolveClick(latlng, sourceEvent);
    return true;
  }

  setMapType(mapType: 'roadmap' | 'satellite' | 'terrain' | 'hybrid') {
    this.mapBaseLayersService.setMapType(mapType);
  }
  set(map: L.Map, userPreferences: userDtos.UserPreferences) {
    this.map = map;
    if (!this.attachedMaps.includes(map)) this.attachedMaps.push(map);
    // ANY click on the map closes the selected item's details panel — the
    // click's origin (tiles, feature paths, fence polygons, markers) does not
    // matter. preclick fires before layer handlers can stop the event, so it
    // even fires for feature paths whose popup click-handler suppresses the
    // map-level click. Clicks inside open popups don't propagate to the map,
    // and after a pan drag Leaflet suppresses the click, so neither closes
    // the panel. Drawing tools (virtual-fence editing, distance measurement)
    // keep the selection while placing vertices.
    map.off('preclick', this.handleMapPreclick, this).on('preclick', this.handleMapPreclick, this);
    this.mapInstance.set(map);

    this.userPreferences = { ...userPreferences };
    this.attachedPreferences.set(map, this.userPreferences);
    this.userPreferences.positionsClustering ??= true;
    this.userPreferences.showMarkers ??= true;
    this.userPreferences.showTracks ??= true;
    this.userPreferences.tracksArrows ??= true;
    this.userPreferences.mapSpeciesIconToggle ??= true;
    this.userPreferences.mapLightConditionIconToggle ??= false;
    this.userPreferences.mapProvider ??= 0;
    this.previousZoom = this.map.getZoom();

    this.mapMarkerService.initialize(map, this.userPreferences);
    this.mapDistanceMeasurementService.initialize(map);
    this.mapBaseLayersService.initialize(map, this.userPreferences);

    // Create dedicated panes for layers
    const unifiedCanvasPane = this.map.createPane('unifiedCanvasPane');
    unifiedCanvasPane.style.zIndex = '610';
    // DOM pass-through: the canvas paints the collar markers/tracks above the
    // interactive vector layers (ESRI feature paths, virtual-fence polygons),
    // so with pointer events it would swallow every click and make those
    // layers unclickable. Collar/track clicks are resolved from map-level
    // events instead (see UnifiedMarkerCanvasLayer#_onClick); drawing tools
    // suspend that handling via DrawingManagerService.activeDrawingMode.
    unifiedCanvasPane.style.pointerEvents = 'none';

    // Cluster Panes
    this.map.createPane('clusterPane').style.zIndex = '620';
    this.map.createPane('clusterLightConditionPane').style.zIndex = '620';
    // Pane for drawing tools
    const drawPane = this.map.createPane('drawPane');
    drawPane.style.zIndex = '700';
    drawPane.style.pointerEvents = 'none';

    // Pane for virtual fence. z-index must be < 600 to be below marker edit handles.
    const vfPane = this.map.createPane('virtualFencePane');
    vfPane.style.zIndex = '450';
    vfPane.style.pointerEvents = 'auto';

    this.setIconScaling();
    this.map.on('moveend', this.handleMoveEnd, this);
    this.setElementStyles();

    this.setMapLayers();
  }

  private setMapLayers() {
    const state = this.userPreferences;

    this.mapBaseLayersService.clearBaseLayers();
    const initialLayer = this.mapBaseLayersService.getInitialLayer();

    if (initialLayer) {
      this.options.layers = [initialLayer];
      this.map.addLayer(initialLayer);
    }

    // Add initial marker layers based on clustering preference
    this.updateClusteredLayers();

    if (
      state.mapSpeciesIconToggle ||
      state.mapLightConditionIconToggle ||
      state.showMarkers ||
      state.showTracks
    ) {
      this.map.addLayer(this.layers.unifiedCanvas);
    }

    if (state.scaleBar && !this.scaleBarMaps.has(this.map)) {
      this.scaleBarMaps.add(this.map);
      L.control.scale().addTo(this.map);
    }
  }

  displayPositions(
    mapPositions: webDtos.MapPositionsList[],
    speciesMap?: Map<number, dsDtos.AnimalSpecies>,
    shouldCallZoomReset = true,
  ) {
    const speciesMapChanged = this.lastSpeciesMap !== speciesMap;
    const hadRenderedItems = this.renderedMapItems.size > 0;
    this.lastSpeciesMap = speciesMap;

    if (!mapPositions?.length) {
      this.softReset();
      return;
    }

    this.mapPositions = mapPositions;

    this.removeStaleRenderedItems(mapPositions);

    mapPositions.forEach((mapPositionList) => {
      const renderedItem = this.renderedMapItems.get(mapPositionList.id);
      if (!speciesMapChanged && renderedItem?.source === mapPositionList) return;

      if (renderedItem) {
        this.removeRenderedItem(mapPositionList.id);
      }

      this.renderedMapItems.set(
        mapPositionList.id,
        this.buildRenderedMapItem(mapPositionList, speciesMap),
      );
    });

    this.updateCanvasLayer();
    this.updateClusteredLayers();

    if (shouldCallZoomReset && !hadRenderedItems) this.resetZoomToPositions();
  }

  setMapPositions(mapPositions: webDtos.MapPositionsList[]) {
    this.mapPositions = mapPositions;
  }

  private buildRenderedMapItem(
    mapPositionList: webDtos.MapPositionsList,
    speciesMap?: Map<number, dsDtos.AnimalSpecies>,
  ): RenderedMapItem {
    const mortalityData: CanvasMarkerData[] = [];
    const speciesIconData: CanvasMarkerData[] = [];
    const standardMarkersData: CanvasMarkerData[] = [];
    const endpointMarkersData: CanvasMarkerData[] = [];
    const allLightConditionsData: CanvasMarkerData[] = [];
    const trackPolylines: TrackPolyline[] = [];

    const { color = '#0000ff', name } = mapPositionList;
    const species = speciesMap?.get(mapPositionList.id);
    const { markerClusterLayer, lightConditionClusterLayer } = this.createClusterLayers(
      mapPositionList.id,
    );
    const trackMarkers: (L.Marker | L.CircleMarker)[] = [];
    const lightConditionMarkers: L.Marker[] = [];

    const { positions } = mapPositionList;
    for (let i = 0; i < positions.length; i++) {
      const position = positions[i];
      const isLastPosition = i === positions.length - 1;
      const isDead = Translator.mortalityStatusShort(position?.mortalityStatus) === 'Dead';
      const nextStatus = isLastPosition
        ? null
        : Translator.mortalityStatusShort(positions[i + 1]?.mortalityStatus);

      if (isDead && (isLastPosition || (nextStatus && nextStatus !== 'Dead'))) {
        mortalityData.push({
          position: position,
          color,
          id: mapPositionList.id,
          collarName: name,
        });
      }

      if (position.latitude != null && position.longitude != null) {
        this.processPosition(
          position,
          i,
          mapPositionList,
          species,
          trackMarkers,
          lightConditionMarkers,
          allLightConditionsData,
          speciesIconData,
        );
      }
    }

    const hideTrack = (mapPositionList as { hideTrack?: boolean }).hideTrack;
    mapPositionList.positions.forEach((p, index) => {
      const isNewest = !hideTrack && index === 0;
      const isOldest = !hideTrack && index === mapPositionList.positions.length - 1;

      if (p.latitude == null || p.longitude == null) return;

      if (isNewest || isOldest) {
        endpointMarkersData.push({
          position: p,
          id: mapPositionList.id,
          collarName: name,
          isNewest,
          isOldest,
        });
      } else {
        standardMarkersData.push({
          position: p,
          color,
          id: mapPositionList.id,
          collarName: name,
        });
      }
    });

    markerClusterLayer.addLayers(trackMarkers);
    lightConditionClusterLayer.addLayers(lightConditionMarkers);
    this.layers.markers.addLayer(markerClusterLayer);
    this.layers.lightCondition.addLayer(lightConditionClusterLayer);

    trackPolylines.push(...this.drawTrackPolyline(mapPositionList, color));

    return {
      source: mapPositionList,
      markerClusterLayer,
      lightConditionClusterLayer,
      mortalityData,
      speciesIconData,
      standardMarkersData,
      endpointMarkersData,
      allLightConditionsData,
      trackPolylines,
    };
  }

  private updateCanvasLayer() {
    const items = [...this.renderedMapItems.values()];
    const standardMarkersData = items.flatMap((item) => item.standardMarkersData);
    const mortalityData = items.flatMap((item) => item.mortalityData);
    const speciesIconData = items.flatMap((item) => item.speciesIconData);
    const allLightConditionsData = items.flatMap((item) => item.allLightConditionsData);
    const endpointMarkersData = items.flatMap((item) => item.endpointMarkersData);
    const trackPolylines = items.flatMap((item) => item.trackPolylines);

    const unifiedData: UnifiedData = {
      standardMarkersData: standardMarkersData.map((d) => ({
        position: d.position,
        id: d.id,
        color: d.color,
        collarName: d.collarName,
      })),
      mortalityData: mortalityData.map((d) => ({
        position: d.position,
        collarName: d.collarName,
        color: d.color,
        id: d.id,
      })),
      speciesIconData: speciesIconData.map((d) => ({
        position: d.position,
        collarName: d.collarName,
        color: d.color,
        icon: d.icon,
        id: d.id,
      })),
      allLightConditionsData: allLightConditionsData.map((d) => ({
        ...d,
        isEndpoint: d.isEndpoint,
      })),
      endpointMarkersData: endpointMarkersData.map((d) => ({
        position: d.position,
        id: d.id,
        collarName: d.collarName,
        isNewest: d.isNewest,
        isOldest: d.isOldest,
      })),
      trackPolylines: trackPolylines,
    };

    this.ensureCanvasLayer();
    const endpointColors = {
      newest: '#' + (this.userPreferences.newestMarkerColor || 'ff0000'),
      oldest: '#' + (this.userPreferences.oldestMarkerColor || '00ff00'),
    };
    this.unifiedCanvasLayer.setData(
      unifiedData,
      this.getToggles(),
      this.mapMarkerService.getAllIconSizes(),
      endpointColors,
    );
  }

  private ensureCanvasLayer() {
    if (this.map && !this.map.hasLayer(this.layers.unifiedCanvas)) {
      this.map.addLayer(this.layers.unifiedCanvas);
    }
    if (this.unifiedCanvasLayer) return;

    this.unifiedCanvasLayer = new UnifiedMarkerCanvasLayer();
    L.setOptions(this.unifiedCanvasLayer, { pane: 'unifiedCanvasPane' });
    // A drawing tool may have suspended interception before the layer existed
    // (no collar positions yet); apply the current state on creation.
    this.unifiedCanvasLayer.setInterceptClicks(this.unifiedCanvasInterceptClicks);
    this.unifiedCanvasLayer.on('click', this.handleUnifiedCanvasClick, this);
    this.unifiedCanvasLayer.on('track-click', this.handleUnifiedCanvasTrackClick, this);
    this.layers.unifiedCanvas.addLayer(this.unifiedCanvasLayer);
  }

  private removeStaleRenderedItems(mapPositions: webDtos.MapPositionsList[]) {
    const nextIds = new Set(mapPositions.map(({ id }) => id));
    [...this.renderedMapItems.keys()].forEach((id) => {
      if (!nextIds.has(id)) this.removeRenderedItem(id);
    });
    const selectedId = this.selectedMapItemId();
    if (selectedId !== undefined && !nextIds.has(selectedId)) {
      this.setSelectedMapItem(undefined);
    }
  }

  private removeRenderedItem(id: number) {
    const renderedItem = this.renderedMapItems.get(id);
    if (!renderedItem) return;

    renderedItem.markerClusterLayer.clearLayers();
    renderedItem.lightConditionClusterLayer.clearLayers();
    this.layers.markers.removeLayer(renderedItem.markerClusterLayer);
    this.layers.lightCondition.removeLayer(renderedItem.lightConditionClusterLayer);
    this.markerClusterLayer = this.markerClusterLayer.filter(
      (layer) => layer !== renderedItem.markerClusterLayer,
    );
    this.lightConditionClusterLayers = this.lightConditionClusterLayers.filter(
      (layer) => layer !== renderedItem.lightConditionClusterLayer,
    );
    this.renderedMapItems.delete(id);
  }

  handleUnifiedCanvasClick({ found, originalEvent, latlng }: UnifiedCanvasClickEvent) {
    if (!found) return;
    const { data, type } = found;
    const { collarName, id, position } = data;

    // Filter out standard points if showMarkers is off, as per original logic's intent
    // for non-interactive elements, although hit detection handles this.
    if (!this.userPreferences.showMarkers && type === 'standard') {
      return;
    }

    if (position) {
      const popupContent = this.mapMarkerService.markerPopup(collarName, position);
      L.popup().setLatLng(latlng).setContent(popupContent).openOn(this.map);
      if (id != null) this.setSelectedMapItem(id);
      // We stop propagation inside the unified layer's _onClick, but doing it here again for safety
      if (originalEvent) L.DomEvent.stop(originalEvent);
    }
  }

  handleUnifiedCanvasTrackClick(e: UnifiedCanvasTrackClickEvent) {
    const trackId = e.found?.data?.id;
    if (trackId == null || !this.unifiedCanvasLayer) {
      return;
    }

    const nextMapItemId = this.selectedMapItemId() === trackId ? undefined : trackId;
    this.setSelectedMapItem(nextMapItemId);
    if (e.originalEvent) L.DomEvent.stop(e.originalEvent);
  }

  setSelectedMapItem(id: number | undefined) {
    this.selectedMapItemId.set(id);
    this.unifiedCanvasLayer?.setSelectedTrack(id ?? null);
  }

  private createClusterLayers(itemId: number) {
    const commonClusterOptions = {
      disableClusteringAtZoom: 18,
      chunkedLoading: true,
      spiderfyOnMaxZoom: false,
      chunkInterval: 160,
      chunkDelay: 50,
      animate: false,
      animateAddingMarkers: false,
      showCoverageOnHover: false,
    };

    const markerClusterLayer = L.markerClusterGroup({
      ...commonClusterOptions,
      clusterPane: 'clusterPane',
    });
    this.bindClusterSelection(markerClusterLayer, itemId);
    this.markerClusterLayer.push(markerClusterLayer);

    const lightConditionClusterLayer = L.markerClusterGroup({
      ...commonClusterOptions,
      clusterPane: 'clusterLightConditionPane',
    });
    this.bindClusterSelection(lightConditionClusterLayer, itemId);
    this.lightConditionClusterLayers.push(lightConditionClusterLayer);

    return { markerClusterLayer, lightConditionClusterLayer };
  }

  private bindClusterSelection(clusterLayer: MarkerClusterGroup, itemId: number) {
    clusterLayer.on('clusterclick', () => {
      this.selectItem(itemId);
    });
  }

  processPosition(
    position: webDtos.GPSPosition,
    index: number,
    mapPositionList: webDtos.MapPositionsList,
    species: dsDtos.AnimalSpecies | undefined,
    trackMarkers: (L.Marker | L.CircleMarker)[],
    lightConditionMarkers: L.Marker[],
    allLightConditionsData: CanvasMarkerData[],
    speciesIconData: CanvasMarkerData[],
  ) {
    const hideTrack = (mapPositionList as { hideTrack?: boolean }).hideTrack;
    const isNewest = !hideTrack && index === 0;
    const isOldest = !hideTrack && index === mapPositionList.positions.length - 1;

    this.addLightConditionData(
      position,
      mapPositionList,
      isNewest,
      isOldest,
      lightConditionMarkers,
      allLightConditionsData,
    );

    const marker = this.mapMarkerService.marker(position, index, mapPositionList);
    if (marker.standard) {
      if (marker.standard instanceof L.CircleMarker) {
        marker.standard.setStyle({ opacity: 0, fillOpacity: 0 });
      }
      trackMarkers.push(marker.standard);
    }

    if (marker.endpoint) {
      this.addEndpointSpeciesIcon(position, mapPositionList, isNewest, species, speciesIconData);
    }
  }

  private addLightConditionData(
    position: webDtos.GPSPosition,
    mapPositionList: webDtos.MapPositionsList,
    isNewest: boolean,
    isOldest: boolean,
    lightConditionMarkers: L.Marker[],
    allLightConditionsData: CanvasMarkerData[],
  ) {
    if (
      position.lightCondition &&
      position.lightCondition !== 'UNDEFINED' &&
      position.lightCondition !== 'UNKNOWN'
    ) {
      const iconPath = this.mapMarkerService.getLightConditionIconPath(position.lightCondition);
      allLightConditionsData.push({
        position,
        collarName: mapPositionList.name,
        id: mapPositionList.id,
        isEndpoint: isNewest || isOldest,
        icon: iconPath,
      });

      // We still need to create DOM markers if clustering is on, but simplified:
      const lightConditionMarker = this.mapMarkerService._lightConditionMarker(
        position,
        mapPositionList.name,
      );
      if (lightConditionMarker) {
        if (isNewest || isOldest) {
          lightConditionMarker.setOpacity(0);
        }
        lightConditionMarkers.push(lightConditionMarker);
      }
    }
  }

  private addEndpointSpeciesIcon(
    position: webDtos.GPSPosition,
    mapPositionList: webDtos.MapPositionsList,
    isNewest: boolean,
    species: dsDtos.AnimalSpecies | undefined,
    speciesIconData: CanvasMarkerData[],
  ) {
    // Add the endpoint marker (newest/oldest) to its layer.
    // Endpoint markers are now drawn on the unified canvas.
    // The logic to hide them when a species icon is shown is also handled by the canvas.
    if (!isNewest || !species?.icon) {
      return;
    }
    speciesIconData.push({
      position,
      color: this.mapMarkerService.darkenColor(mapPositionList.color, 0.3),
      icon: species.icon,
      id: mapPositionList.id,
      collarName: mapPositionList.name,
    });
  }

  private drawTrackPolyline(
    mapPositionList: webDtos.MapPositionsList & { hideTrack?: boolean },
    color: string,
  ): TrackPolyline[] {
    if (mapPositionList.hideTrack) return [];
    const latlngs = mapPositionList.positions
      .filter((p) => p.latitude != null && p.longitude != null)
      .map((p) => [p.latitude, p.longitude] as L.LatLngExpression)
      .reverse(); // Reverse to be oldest-to-newest

    // Return the data needed for the unified canvas to draw the polyline and its arrows.
    // No L.Polyline object is created here anymore.
    if (latlngs.length === 0) return [];
    return [{ latlngs, color, id: mapPositionList.id }];
  }

  toggleClustering(enabled: boolean) {
    // Cancel any pending zoom-related redraws to prevent race conditions when switching modes.
    clearTimeout(this.zoomDebounceTimer);

    this.userPreferences.positionsClustering = enabled;
    this.updateClusteredLayers();

    // Ensure unified canvas is on the map and redrawn with the current visibility toggles.
    // The unified canvas is responsible for drawing non-clustered points, species icons,
    // and mortality icons, so it should always be present and updated.
    // Its internal logic will decide what to draw based on the toggles.
    if (!this.map.hasLayer(this.layers.unifiedCanvas)) {
      this.map.addLayer(this.layers.unifiedCanvas);
    }

    // Redraw the unified canvas layer with current settings, regardless of clustering state.
    this.unifiedCanvasLayer.redraw(this.getToggles(), this.mapMarkerService.getAllIconSizes());

    this.userPreferences.positionsClustering = enabled;
  }

  private updateClusteredLayers() {
    const clusteringEnabled = this.userPreferences.positionsClustering;
    const lightConditionToggle = this.userPreferences.mapLightConditionIconToggle;
    const showMarkers = this.userPreferences.showMarkers;
    const shouldShowLightCondition = clusteringEnabled && lightConditionToggle;
    const shouldShowMarkers = clusteringEnabled && !lightConditionToggle && showMarkers;

    if (shouldShowLightCondition) {
      if (this.map.hasLayer(this.layers.markers)) {
        this.map.removeLayer(this.layers.markers);
      }
      if (!this.map.hasLayer(this.layers.lightCondition)) {
        this.map.addLayer(this.layers.lightCondition);
      }
      return;
    }

    if (shouldShowMarkers) {
      if (this.map.hasLayer(this.layers.lightCondition)) {
        this.map.removeLayer(this.layers.lightCondition);
      }
      if (!this.map.hasLayer(this.layers.markers)) {
        this.map.addLayer(this.layers.markers);
      }
      return;
    }

    if (this.map.hasLayer(this.layers.markers)) {
      this.map.removeLayer(this.layers.markers);
    }
    if (this.map.hasLayer(this.layers.lightCondition)) {
      this.map.removeLayer(this.layers.lightCondition);
    }
  }

  private getToggles(): UnifiedDrawToggles {
    const isLcOn = this.userPreferences.mapLightConditionIconToggle;
    const isMarkersOn = this.userPreferences.showMarkers;

    return {
      // CANVAS DRAWING INSTRUCTIONS (Prevent double-drawing)
      // Always draw on canvas. The DOM markers are now invisible.
      showMarkers: isMarkersOn && !isLcOn,
      mapLightConditionToggle: isLcOn,

      // VISIBILITY PREFERENCES (User Intent)
      // If the user WANTS markers (or LC), we should show mortality,
      // regardless of whether clustering is hiding the standard dots.
      showMortality: isMarkersOn || isLcOn,
      // The user's preference for showing tracks.
      showTracks: this.userPreferences.showTracks,
      mapSpeciesIconToggle: this.userPreferences.mapSpeciesIconToggle,
      // Arrows should only be shown if both tracks and arrows are toggled on.
      tracksArrows: this.userPreferences.showTracks && this.userPreferences.tracksArrows,
    };
  }

  toggleMapLayer(
    layerName:
      | 'speciesIcon'
      | 'tracks'
      | 'trackArrows'
      | 'markers'
      | 'clustering'
      | 'lightCondition',
    isEnabled: boolean,
  ) {
    // Cancel any pending zoom-related redraws to prevent race conditions.
    clearTimeout(this.zoomDebounceTimer);

    switch (layerName) {
      case 'speciesIcon':
        this.userPreferences.mapSpeciesIconToggle = isEnabled;
        this.unifiedCanvasLayer.redraw(this.getToggles(), this.mapMarkerService.getAllIconSizes());
        break;

      case 'lightCondition':
        this.userPreferences.mapLightConditionIconToggle = isEnabled;
        this.updateClusteredLayers();
        this.unifiedCanvasLayer.redraw(this.getToggles(), this.mapMarkerService.getAllIconSizes());
        break;
      case 'tracks':
        this.userPreferences.showTracks = isEnabled;
        this.unifiedCanvasLayer.redraw(this.getToggles(), this.mapMarkerService.getAllIconSizes());
        break;
      case 'trackArrows':
        this.userPreferences.tracksArrows = isEnabled;
        this.unifiedCanvasLayer.redraw(this.getToggles(), this.mapMarkerService.getAllIconSizes());
        break;
      case 'markers': {
        this.userPreferences.showMarkers = isEnabled;
        this.updateClusteredLayers();
        this.unifiedCanvasLayer.redraw(this.getToggles(), this.mapMarkerService.getAllIconSizes());
        break;
      }
      case 'clustering':
        this.toggleClustering(isEnabled);
        break;
    }
    this.layerChanges.update((v) => v + 1);
  }

  toggleGpsLayers(isEnabled: boolean) {
    clearTimeout(this.zoomDebounceTimer);
    this.userPreferences.showMarkers = isEnabled;
    this.userPreferences.showTracks = isEnabled;
    this.userPreferences.tracksArrows = isEnabled;
    if (!isEnabled) {
      this.userPreferences.mapLightConditionIconToggle = false;
    }
    this.updateClusteredLayers();
    this.unifiedCanvasLayer.redraw(this.getToggles(), this.mapMarkerService.getAllIconSizes());
    this.layerChanges.update((v) => v + 1);
  }

  zoomToItem(_id?: number) {
    if (!_id) return;

    this.resetZoomToPositions(_id);
  }

  selectItem(id?: number) {
    if (!id) return;

    this.setSelectedMapItem(id);
  }

  requestFocusItem(id?: number) {
    if (!id) return;

    this.requestedFocusItemId.set(id);
    this.selectItem(id);
  }

  clearRequestedFocusItem() {
    this.requestedFocusItemId.set(undefined);
  }

  focusRequestedItem(mapPositions: webDtos.MapPositionsList[]) {
    const requestedId = this.requestedFocusItemId();
    if (!requestedId) return;

    if (this.selectedMapItemId() !== requestedId) {
      this.clearRequestedFocusItem();
      return;
    }

    const hasPositions = mapPositions.some(
      ({ id, positions }) => id === requestedId && Boolean(positions?.length),
    );
    if (!hasPositions) return;

    this.requestedFocusItemId.set(undefined);
    this.focusItem(requestedId);
  }

  focusItem(id?: number) {
    if (!id) return;

    this.selectItem(id);
    this.zoomToItem(id);
  }

  setIconScaling() {
    this.map.on('zoomend', this.handleZoomEnd, this);
  }

  private handleZoomEnd() {
    clearTimeout(this.zoomDebounceTimer);

    const currentZoom = this.map.getZoom();
    const crossedSpeciesIconThreshold = this.mapMarkerService.speciesZoomConfig.some(
      ({ threshold }) =>
        (this.previousZoom < threshold && currentZoom >= threshold) ||
        (this.previousZoom >= threshold && currentZoom < threshold),
    );
    const crossedMortalityThreshold = this.mapMarkerService.mortalityZoomConfig.some(
      ({ threshold }) =>
        (this.previousZoom < threshold && currentZoom >= threshold) ||
        (this.previousZoom >= threshold && currentZoom < threshold),
    );
    const crossedLightConditionThreshold = this.mapMarkerService.lightConditionZoomConfig.some(
      ({ threshold }) =>
        (this.previousZoom < threshold && currentZoom >= threshold) ||
        (this.previousZoom >= threshold && currentZoom < threshold),
    );

    const crossedThreshold =
      crossedSpeciesIconThreshold || crossedMortalityThreshold || crossedLightConditionThreshold;

    if (!crossedThreshold && this.previousZoom !== currentZoom) {
      this.previousZoom = currentZoom;
      return;
    }

    this.zoomDebounceTimer = setTimeout(() => {
      const iconSizes = this.mapMarkerService.getAllIconSizes();
      // Redraw the canvas layer on zoom end to update icon sizes and positions.
      if (this.unifiedCanvasLayer) {
        this.unifiedCanvasLayer.redraw(this.getToggles(), iconSizes);
      }
      this.previousZoom = currentZoom;
    }, 400);
  }

  private handleMoveEnd() {
    // Debounce the redraw on move end as well
    clearTimeout(this.zoomDebounceTimer);
    this.zoomDebounceTimer = setTimeout(() => {
      if (this.unifiedCanvasLayer) {
        this.unifiedCanvasLayer.redraw(this.getToggles(), this.mapMarkerService.getAllIconSizes());
      }
    }, 300);
  }

  resetZoom() {
    if (!this.map) return;
    this.resetZoomToPositions();
  }

  refreshMap() {
    // Determine if the map needs a size update
    if (this.map) {
      this.map.invalidateSize();
    }
  }

  resetZoomToPositions(idToZoom?: number) {
    if (!this.map) return;

    let positions: webDtos.MapPositionsList[];

    if (idToZoom !== undefined) {
      const selectedItem = this.mapPositions?.find(({ id }) => id === idToZoom);
      positions = selectedItem ? [selectedItem] : [];
    } else {
      positions = this.mapPositions || [];
    }

    const allLatLngs: L.LatLngExpression[] = [];
    positions.forEach((mapPositionList) => {
      mapPositionList.positions.forEach((position) => {
        if (position.latitude != null && position.longitude != null) {
          allLatLngs.push([position.latitude, position.longitude]);
        }
      });
    });

    if (allLatLngs.length === 0) {
      this.map.setView(L.latLng(30, 0), 3);
      return;
    }

    const mapContainer = this.map.getContainer();

    const runFitBounds = () => {
      this.map.invalidateSize({ pan: false, animate: false, debounceMoveend: true });

      const bounds = L.latLngBounds(allLatLngs);

      this.map.fitBounds(bounds, {
        paddingTopLeft: [180, 50], // [left, top]
        paddingBottomRight: [50, 50], // [right, bottom]
        maxZoom: 18,
        animate: false,
      });
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize({ animate: false });
        }
      }, 100);
    };

    // Check if the map currently has dimensions
    if (mapContainer.clientWidth > 0 && mapContainer.clientHeight > 0) {
      runFitBounds();
    } else {
      // Only wait for resize if the map is currently hidden/collapsed
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          runFitBounds();
          observer.disconnect();
        }
      });
      observer.observe(mapContainer);
    }
  }

  private setElementStyles() {
    this.zoomElement = document.querySelector('.leaflet-top.leaflet-left');
    if (this.mobileService.isOn()) {
      this.zoomElement?.classList.add('sidebar-hidden');
    } else {
      this.zoomElement?.classList.add('sidebar-shown');
    }
  }

  onLeftSidebarToggle(sideBarHidden: boolean) {
    if (!this.zoomElement) return;
    const { classList } = this.zoomElement;
    if (sideBarHidden) {
      classList.remove('sidebar-shown');
      classList.add('sidebar-hidden');
    } else {
      classList.remove('sidebar-hidden');
      classList.add('sidebar-shown');
    }
  }

  reset(map?: L.Map) {
    if (map) {
      this.attachedMaps = this.attachedMaps.filter((attached) => attached !== map);
      this.attachedPreferences.delete(map);
      // A newer base-map instance owns the shared state; a stale teardown
      // must not clear it.
      if (map !== this.map) return;
      const surviving = this.attachedMaps[this.attachedMaps.length - 1];
      if (surviving) {
        // A still-mounted map survives (e.g. the page under a just-closed
        // modal): its layers died with the destroyed map, so re-point the
        // services and re-mount base + marker layers on it. The panes and the
        // moveend handler are already bound on the surviving instance.
        // The base-layer service is re-pointed FIRST — it still references
        // the destroyed map here (the external-layers service's reset, which
        // also reattaches it, runs later in the same teardown) — and the
        // surviving map's own preferences snapshot is restored with it, so
        // clearBaseLayers/getInitialLayer operate on the right map and the
        // right preference object instead of the closed modal's.
        this.map = surviving;
        this.mapInstance.set(surviving);
        this.userPreferences = this.attachedPreferences.get(surviving) ?? this.userPreferences;
        this.mapBaseLayersService.initialize(surviving, this.userPreferences);
        this.setMapLayers();
        this.setIconScaling();
        return;
      }
    }
    this.mapMarkerService.popupCustomizer = undefined;
    this.selectedMapItemId.set(undefined);
    this.map?.eachLayer((layer) => this.map.removeLayer(layer));
    this.softReset();
    this.map = undefined;
    this.mapInstance.set(undefined);
    this.userPreferences = undefined;
    this.attachedMaps = [];
    this.attachedPreferences.clear();
  }

  softReset(preservePositions = false) {
    this.setSelectedMapItem(undefined);
    // Clear all cluster layers
    this.markerClusterLayer.forEach((cl) => cl.clearLayers());
    this.lightConditionClusterLayers.forEach((cl) => cl.clearLayers());

    for (const key in this.layers) {
      if (key !== 'polygons') {
        // The 'track' layer is gone, so no need to check for it.
        if (this.layers[key]) {
          this.layers[key]?.clearLayers();
        }
      }
    }

    this.markerClusterLayer = [];
    if (!preservePositions) {
      this.mapPositions = undefined;
    }
    this.lightConditionClusterLayers = [];
    this.renderedMapItems.clear();
    this.lastSpeciesMap = undefined;
    if (this.unifiedCanvasLayer) {
      this.unifiedCanvasLayer.remove(); // Remove the single canvas layer
      this.unifiedCanvasLayer = null;
    }
  }
}
